import {
    Component,
    Input,
    OnInit,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    ChangeDetectorRef
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';

import { IconName } from './icon-registry';
import { IconCacheService } from './fcp-icon-cache.service';

@Component({
    selector: 'fcp-icon-svg',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <svg
            *ngIf="svgContent | async as svgContent"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            [attr.data-icon]="icon"
            [innerHTML]="svgContent"
            [ngClass]="[colorClass, sizeClass, 'fill-current', 'inline-block']"
        ></svg>
    `,
    styleUrls: ['./fcp-icon.component.less'],
    standalone: false
})
export class FcpIconComponentSVGComponent implements OnInit, OnChanges {
    @Input() icon: IconName;
    @Input() color: FcpIconSVGColors = FcpIconSVGColors.Neutral;
    @Input() size: FcpIconSVGSizes = FcpIconSVGSizes.Large;

    public colorClass: string;
    public sizeClass: string;
    public svgContent: Observable<SafeHtml>;

    constructor(
        private iconCacheService: IconCacheService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.updateIconProperties();
        this.loadSvg(this.icon);
        this.cdr.markForCheck();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.icon || changes.color) {
            this.updateIconProperties();
            this.loadSvg(this.icon);
            this.cdr.markForCheck();
        } else if (changes.color || changes.size) {
            this.updateIconProperties();
            this.cdr.markForCheck();
        }
    }

    private updateIconProperties() {
        const sizeClasses = {
            [FcpIconSVGSizes.Small]: 'size-small',
            [FcpIconSVGSizes.Large]: 'size-large',
            [FcpIconSVGSizes.ExtraLarge]: 'size-extra-large'
        };
        this.sizeClass = sizeClasses[this.size];

        const colorClasses = {
            [FcpIconSVGColors.Prominent]: 'text-prominent',
            [FcpIconSVGColors.Neutral]: 'text-neutral',
            [FcpIconSVGColors.NeutralDark]: 'text-neutral-dark',
            [FcpIconSVGColors.NeutralLight]: 'text-neutral-light',
            [FcpIconSVGColors.NeutralDarker]: 'text-neutral-darker',
            [FcpIconSVGColors.Error]: 'text-error',
            [FcpIconSVGColors.Warning]: 'text-warning',
            [FcpIconSVGColors.Success]: 'text-success',
            [FcpIconSVGColors.Information]: 'text-information',
            [FcpIconSVGColors.Disabled]: 'text-disabled'
        };
        this.colorClass = colorClasses[this.color];
    }

    private loadSvg(iconName: IconName) {
        this.svgContent = this.iconCacheService.getIcon(iconName);
        this.cdr.markForCheck();
    }
}

export enum FcpIconSVGSizes {
    Small = 'small',
    Large = 'large',
    ExtraLarge = 'extra-large'
}

export enum FcpIconSVGColors {
    Prominent = 'prominent',
    Neutral = 'neutral',
    NeutralDark = 'neutral-dark',
    NeutralLight = 'neutral-light',
    NeutralDarker = 'neutral-darker',
    Error = 'error',
    Warning = 'warning',
    Success = 'success',
    Information = 'information',
    Disabled = 'disabled'
}
